import React, { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import PageShell from "../dashboard/PageShell";

const SelectAcceptanceCouncil: React.FC = () => {
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      return parts[parts.length - 3] || "";
    } catch {
      return "";
    }
  }, []);

  const [form, setForm] = useState({
    councilId: "",
    decisionNumber: "",
    decisionSignedDate: "",
    acceptanceDate: "",
    evaluationResult: "Đúng tiến độ",
    classification: "",
    evaluationScore: "",
    products: "",
    recommendations: "",
    attachments: [] as File[],
  });

  const change = (field: string, value: string | File[]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);
      change("attachments", [...form.attachments, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    const updated = form.attachments.filter((_, i) => i !== index);
    change("attachments", updated);
  };

  return (
    <PageShell title="Chọn Hội đồng nghiệm thu cấp đơn vị">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => window.history.back()}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800">
              Chọn Hội đồng nghiệm thu cấp đơn vị
            </h1>
          </div>
        </div>

        {/* Topic header */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-800 font-medium">Tên đề tài:</div>
              <div className="text-gray-700">
                Nghiên cứu xác định nguyên nhân và đề xuất giải pháp khoa học
                công nghệ nhằm ổn định các cửa sông và vùng bờ biển tỉnh Bình
                Thuận
              </div>
            </div>
            <div>
              <div className="text-gray-800 font-medium">Chủ nhiệm:</div>
              <div className="text-gray-700">ThS Nguyễn Văn An</div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg p-6">
          <div className="space-y-6">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chọn hội đồng đánh giá
                </label>
                <select
                  value={form.councilId}
                  onChange={(e) => change("councilId", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">- Chọn hội đồng đánh giá-</option>
                  <option value="council1">Hội đồng 1</option>
                  <option value="council2">Hội đồng 2</option>
                  <option value="council3">Hội đồng 3</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Số quyết định <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={form.decisionNumber}
                  onChange={(e) => change("decisionNumber", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập số quyết định"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kết quả đánh giá
                </label>
                <select
                  value={form.evaluationResult}
                  onChange={(e) => change("evaluationResult", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Đúng tiến độ">Đúng tiến độ</option>
                  <option value="Chậm tiến độ">Chậm tiến độ</option>
                  <option value="Vượt tiến độ">Vượt tiến độ</option>
                </select>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Xếp loại
                </label>
                <select
                  value={form.classification}
                  onChange={(e) => change("classification", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">- Xếp loại -</option>
                  <option value="Xuất sắc">Xuất sắc</option>
                  <option value="Tốt">Tốt</option>
                  <option value="Khá">Khá</option>
                  <option value="Trung bình">Trung bình</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Điểm đánh giá
                </label>
                <input
                  type="text"
                  value={form.evaluationScore}
                  onChange={(e) => change("evaluationScore", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập điểm đánh giá"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ngày ký QĐ TLHĐ
                </label>
                <input
                  type="text"
                  value={form.decisionSignedDate}
                  onChange={(e) => change("decisionSignedDate", e.target.value)}
                  placeholder="mm/dd/yyyy"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ngày nghiệm thu
                </label>
                <input
                  type="text"
                  value={form.acceptanceDate}
                  onChange={(e) => change("acceptanceDate", e.target.value)}
                  placeholder="mm/dd/yyyy"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Row 3 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Các sản phẩm đạt được của đề tài
              </label>
              <textarea
                value={form.products}
                onChange={(e) => change("products", e.target.value)}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nhập các sản phẩm đạt được của đề tài..."
              />
            </div>

            {/* Row 4 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nội dung kiến nghị, đề xuất của Hội đồng
              </label>
              <textarea
                value={form.recommendations}
                onChange={(e) => change("recommendations", e.target.value)}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nhập nội dung kiến nghị, đề xuất của Hội đồng..."
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File đính kèm
              </label>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium cursor-pointer">
                  Choose Files
                </label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                  id="file-upload"
                />
                <span className="text-sm text-gray-500">
                  {form.attachments.length === 0
                    ? "No file chosen"
                    : `${form.attachments.length} file(s) chosen`}
                </span>
              </div>

              {form.attachments.length > 0 && (
                <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                  {form.attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-md"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                        title="Xóa file"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8 pt-6">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              onClick={() => {
                console.log("Save acceptance council selection", form);
                const projectId = id || window.location.pathname.split("/")[4];
                window.location.href = `/topics/approval/detail/${projectId}`;
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
            >
              Lưu dữ liệu
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default SelectAcceptanceCouncil;
